import React, { createContext, useContext, useState, useEffect } from 'react';

const StatsContext = createContext();

const STATS_KEY = 'wordle6-stats';

const initialStats = {
  gamesPlayed: 0,
  gamesWon: 0,
  currentStreak: 0,
  maxStreak: 0,
  guessDistribution: {}, // {1: 0, 2: 0, 3: 0...} guess number -> count
  lastGameNumber: null,
  lastPlayedDate: null,
};

function loadStats() {
  try {
    const saved = localStorage.getItem(STATS_KEY);
    if (saved) {
      return { ...initialStats, ...JSON.parse(saved) };
    }
  } catch (error) {
    console.error('Error loading stats:', error);
  }
  return initialStats;
}

function saveStats(stats) {
  try {
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch (error) {
    console.error('Error saving stats:', error);
  }
}

export function StatsProvider({ children }) {
  const [stats, setStats] = useState(loadStats);

  useEffect(() => {
    saveStats(stats);
  }, [stats]);

  const recordGame = (gameNumber, won, guessCount) => {
    setStats(prevStats => {
      // Prevent recording same game twice
      if (prevStats.lastGameNumber === gameNumber) {
        return prevStats;
      }

      const today = new Date().toDateString();
      let newStreak = prevStats.currentStreak;

      if (won) {
        // Update streak based on game number continuity
        if (prevStats.lastGameNumber === null) {
          // First game ever
          newStreak = 1;
        } else if (gameNumber === prevStats.lastGameNumber + 1) {
          // Consecutive game number (played daily)
          newStreak = prevStats.currentStreak + 1;
        } else {
          // Gap in game numbers (missed days)
          newStreak = 1;
        }

        // Update guess distribution (object notation: {1: count, 2: count, ...})
        const newDistribution = { ...prevStats.guessDistribution };
        newDistribution[guessCount] = (newDistribution[guessCount] || 0) + 1;

        return {
          gamesPlayed: prevStats.gamesPlayed + 1,
          gamesWon: prevStats.gamesWon + 1,
          currentStreak: newStreak,
          maxStreak: Math.max(prevStats.maxStreak, newStreak),
          guessDistribution: newDistribution,
          lastGameNumber: gameNumber,
          lastPlayedDate: today,
        };
      } else {
        // Lost - reset streak
        return {
          ...prevStats,
          gamesPlayed: prevStats.gamesPlayed + 1,
          currentStreak: 0,
          lastGameNumber: gameNumber,
          lastPlayedDate: today,
        };
      }
    });
  };

  const getWinPercentage = () => {
    if (stats.gamesPlayed === 0) return 0;
    return Math.round((stats.gamesWon / stats.gamesPlayed) * 100);
  };

  const resetStats = () => {
    setStats(initialStats);
    localStorage.removeItem(STATS_KEY);
  };

  const value = {
    stats,
    recordGame,
    getWinPercentage,
    resetStats,
  };

  return (
    <StatsContext.Provider value={value}>
      {children}
    </StatsContext.Provider>
  );
}

export function useStats() {
  const context = useContext(StatsContext);
  if (!context) {
    throw new Error('useStats must be used within a StatsProvider');
  }
  return context;
}